import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner, Button, Card, Chip } from '@cowoo/shared/ui-web';
import { ApiProblem, formatDayShort, formatTime, todayBerlin } from '@cowoo/shared';
import { api } from '../../lib/api';
import { Dialog, Field, Icon, Table, Td, Th, fieldClass } from '../../ui';
import { STATUS_TONE, objectLabel, whoLabel } from '../bookings/bookingRow';

/**
 * "Heute" and "Aktive Sperrungen" (10-web-admin § 5.2).
 *
 * The two live cards under the plan grid: what is booked in the building right
 * now, and what is out of service. They sit together because they answer the
 * same question from opposite sides — a block is only safe where nothing is
 * booked, and the server refuses one that is not.
 */
export function TodayAndBlocks({
  locationId,
  prefill,
}: {
  locationId: string | null;
  /** § 5.3 — a free window of the rebuild calendar, handed over as the dialog's dates. */
  prefill?: { from: string; to: string; nonce: number } | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [resourceId, setResourceId] = useState('');
  const [type, setType] = useState<'blocked' | 'maintenance'>('maintenance');
  const [fromDay, setFromDay] = useState(new Date().toISOString().slice(0, 10));
  const [toDay, setToDay] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<ApiProblem | null>(null);

  // The nonce lets the same window open the dialog twice in a row.
  useEffect(() => {
    if (!prefill) return;
    setEditing(null);
    setResourceId('');
    setReason('');
    setFromDay(prefill.from);
    setToDay(prefill.to);
    setProblem(null);
    setDialog(true);
  }, [prefill]);

  const today = useQuery({
    queryKey: ['admin', 'bookings', 'today'],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/bookings/today');
      if (error) throw error;
      return data.items;
    },
  });

  const blocks = useQuery({
    queryKey: ['admin', 'resource-blocks', locationId],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/resource-blocks', {
        params: { query: locationId ? { locationId } : {} },
      });
      if (error) throw error;
      return data.items;
    },
  });

  /**
   * § 5.2 — "Objekt" is a select of **resources**, not of bookings.
   *
   * The published plan of the location is where they come from: it is what
   * exists to be blocked, and it is already served for the member's picker, so
   * the two cannot disagree about what a desk is called.
   */
  const resources = useQuery({
    queryKey: ['admin', 'plan-resources', locationId],
    enabled: locationId !== null,
    queryFn: async () => {
      const { data, error } = await api.GET('/locations/{id}/plan', {
        params: { path: { id: locationId! } },
      });
      if (error) throw error;
      return data.plans.flatMap((plan) => plan.placements);
    },
  });

  const close = () => {
    setDialog(false);
    setEditing(null);
    setProblem(null);
    void queryClient.invalidateQueries({ queryKey: ['admin', 'resource-blocks'] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const body = { resourceId, type, fromDay, toDay, reason: reason.trim() };
      const { error } = editing
        ? await api.PATCH('/admin/resource-blocks/{id}', { params: { path: { id: editing } }, body })
        : await api.POST('/admin/resource-blocks', { body });
      if (error) throw error;
    },
    onSuccess: close,
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE('/admin/resource-blocks/{id}', { params: { path: { id } } });
      if (error) throw error;
    },
    onSuccess: close,
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  /**
   * § 5.2 — the server answers a refused block with the bookings in the way,
   * and the dialog says how many and where to find them. It does not offer to
   * rebook: a block is version-independent, so nobody is moved automatically.
   */
  const conflicts = problem?.ext.bookings ?? [];

  const items = today.data ?? [];

  return (
    <>
      <Card>
        {/* § 5.2 names the day in the heading — "Heute" alone does not say
            which day the table is about when it is read in a screenshot. */}
        <h2 className="font-heading text-ink mb-3 text-lg">
          {t('admin.plans.bookingsToday', { dayShort: formatDayShort(todayBerlin()) })}
        </h2>
        {items.length === 0 ? (
          <p className="font-body text-muted text-sm">{t('admin.plans.todayEmpty')}</p>
        ) : (
          <Table
            head={
              <>
                <Th>{t('admin.bookings.column.object')}</Th>
                <Th>{t('admin.bookings.column.plan')}</Th>
                <Th>{t('admin.bookings.column.who')}</Th>
                <Th>{t('admin.bookings.column.time')}</Th>
                <Th>{t('admin.bookings.column.status')}</Th>
              </>
            }
          >
            {items.map((booking) => (
              <tr key={booking.id} className="hover:bg-fog">
                <Td bold>{objectLabel(booking, t)}</Td>
                <Td>{booking.planName ?? '—'}</Td>
                <Td>{whoLabel(booking, t)}</Td>
                <Td>
                  {booking.blockStart === null ? t('admin.bookings.allDay') : `${booking.blockStart} Uhr`}
                </Td>
                <Td>
                  <Chip
                    tone={STATUS_TONE[booking.status] ?? 'neutral'}
                    label={
                      booking.checkedInAt
                        ? `${t('admin.bookings.state.checked_in')} ${formatTime(booking.checkedInAt)}`
                        : t(`admin.bookings.state.${booking.status}`)
                    }
                  />
                </Td>
              </tr>
            ))}
          </Table>
        )}
        <p className="pt-2">
          <Link to="/buchungen" className="text-teal-dark font-body text-sm underline">
            {t('admin.plans.allBookings')}
          </Link>
        </p>
      </Card>

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-ink text-lg">{t('admin.plans.blocks')}</h2>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setEditing(null);
              setResourceId('');
              setReason('');
              setDialog(true);
            }}
          >
            {t('admin.plans.blockNew')}
          </Button>
        </div>

        {(blocks.data ?? []).length === 0 ? (
          <p className="font-body text-muted text-sm">{t('admin.plans.noBlocks')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {(blocks.data ?? []).map((block) => (
              <li key={block.id} className="border-hairline flex items-center gap-3 border-b pb-2">
                <Chip
                  tone={block.type === 'maintenance' ? 'warn' : 'danger'}
                  label={t(`admin.plans.blockType_${block.type}`)}
                />
                <span className="font-body text-ink text-sm">
                  {[block.label, block.zone].filter(Boolean).join(' · ') || '—'}
                </span>
                <span className="font-body text-muted flex-1 text-sm">
                  {t('admin.plans.blockRange', {
                    from: formatDayShort(block.fromDay),
                    to: formatDayShort(block.toDay),
                  })}
                  {block.reason ? ` · ${block.reason}` : ''}
                </span>
                {/* § 5.2 — the row is editable, not only removable: a block
                    that needs another two days is the common case. */}
                <button
                  type="button"
                  className="text-muted hover:text-ink font-body text-sm font-semibold"
                  onClick={() => {
                    setEditing(block.id);
                    setResourceId(block.resourceId);
                    setType(block.type as 'blocked' | 'maintenance');
                    setFromDay(block.fromDay);
                    setToDay(block.toDay);
                    setReason(block.reason ?? '');
                    setDialog(true);
                  }}
                >
                  <Icon name="chevronRight" size={16} />
                  <span className="sr-only">{t('common.change')}</span>
                </button>
                <button
                  type="button"
                  className="text-muted hover:text-danger-text font-body text-sm font-semibold"
                  onClick={() => remove.mutate(block.id)}
                >
                  {t('admin.plans.blockRemove')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {dialog ? (
        <Dialog
          title={editing ? t('admin.plans.blockEdit') : t('admin.plans.blockNew')}
          confirmLabel={t('common.save')}
          onConfirm={() => save.mutate()}
          onClose={close}
          busy={save.isPending}
          disabled={resourceId === '' || reason.trim().length < 5 || toDay < fromDay}
        >
          {problem ? (
            <Banner tone="danger">
              {conflicts.length > 0
                ? t('admin.plans.blockConflict', { count: conflicts.length })
                : t(problem.i18nKey)}
            </Banner>
          ) : null}
          {conflicts.length > 0 ? (
            <Link to="/buchungen" className="text-teal-dark font-body text-sm underline">
              {t('admin.plans.blockConflictLink')}
            </Link>
          ) : null}

          <Field label={t('admin.plans.blockResource')}>
            <select
              value={resourceId}
              onChange={(event) => setResourceId(event.target.value)}
              className={fieldClass}
            >
              <option value="">—</option>
              {(resources.data ?? []).map((resource) => (
                <option key={resource.resourceId} value={resource.resourceId}>
                  {[resource.label, resource.zone].filter(Boolean).join(' · ')}
                </option>
              ))}
            </select>
          </Field>

          <div className="flex gap-2.5">
            <div className="flex-1">
              <Field label={t('admin.plans.blockType')}>
                <select
                  value={type}
                  onChange={(event) => setType(event.target.value as 'blocked' | 'maintenance')}
                  className={fieldClass}
                >
                  <option value="maintenance">{t('admin.plans.blockType_maintenance')}</option>
                  <option value="blocked">{t('admin.plans.blockType_blocked')}</option>
                </select>
              </Field>
            </div>
            <div className="flex-1">
              <Field label={t('admin.plans.blockFrom')}>
                <input
                  type="date"
                  value={fromDay}
                  onChange={(event) => setFromDay(event.target.value)}
                  className={fieldClass}
                />
              </Field>
            </div>
            <div className="flex-1">
              <Field label={t('admin.plans.blockTo')}>
                <input
                  type="date"
                  value={toDay}
                  onChange={(event) => setToDay(event.target.value)}
                  className={fieldClass}
                />
              </Field>
            </div>
          </div>

          <Field label={t('admin.plans.blockReason')} hint={t('admin.reasonHint')}>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('admin.reasonPlaceholder')}
              className={fieldClass}
            />
          </Field>
        </Dialog>
      ) : null}
    </>
  );
}
